import React from 'react';
import Toast from '../components/Toast';
import { useParams, useNavigate } from 'react-router-dom';
import authService from '../services/authService';

const BAIQLExecutionMonitor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState('queued');
  const [batchType, setBatchType] = React.useState(null);
  const [mode, setMode] = React.useState('EXECUTE');
  const [current, setCurrent] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [elapsed, setElapsed] = React.useState(0);
  const [logs, setLogs] = React.useState([]);
  const [pending, setPending] = React.useState(false);
  const [toast, setToast] = React.useState({ show:false, message:'', type:'success' });
  const [lastError, setLastError] = React.useState(null);
  // SCREEN modal state
  const [screen, setScreen] = React.useState({ show:false, file:'', format:'txt', content:'', ready:false, saving:false });
  const [screenHandled, setScreenHandled] = React.useState(false);
  const handledScreenSigs = React.useRef(new Set()); // Track all handled screen signatures to prevent loops
  const lastErrorCheckRef = React.useRef('');
  const previousStatusRef = React.useRef(''); // Track previous status to detect transitions
  const screenHandledRef = React.useRef(false); // Track screenHandled in ref to avoid dependency
  const finalScreenShownRef = React.useRef(false); // Track if we've shown the final/completed screen

  // Separate effect to check balance when execution fails
  React.useEffect(() => {
    if (status !== 'failed') return;
    
    const checkBalance = async () => {
      try {
        const t = authService.getToken && authService.getToken();
        if (!t) return;
        
        const balRes = await fetch('/api/billing/balance', { 
          headers: { Authorization: `Bearer ${t}` }, 
          credentials:'include', 
          cache:'no-store' 
        });
        
        if (balRes.ok) {
          const balData = await balRes.json();
          const balanceCents = typeof balData?.balanceCents === 'number' ? balData.balanceCents : null;
          
          // If balance is zero or negative, show insufficient balance message
          if (balanceCents !== null && balanceCents <= 0) {
            const errorSig = `insufficient_${id}_${Date.now()}`;
            if (lastErrorCheckRef.current !== errorSig) {
              lastErrorCheckRef.current = errorSig;
              setToast({ 
                show: true, 
                type: 'error', 
                message: 'Execution failed due to insufficient balance. Please add funds to your account to continue.',
                duration: 10000
              });
            }
          }
        }
      } catch (err) {
        // Silently fail - don't block error display
      }
    };
    
    checkBalance();
  }, [status, id]);

  React.useEffect(() => {
    let mounted = true;
    let start = Date.now();
    const tick = setInterval(() => { if (mounted && (status === 'queued' || status === 'running')) setElapsed(Math.floor((Date.now()-start)/1000)); }, 1000);

    const poll = async () => {
      if (!mounted) return; // Early exit if unmounted
      
      let shouldStop = false;
      let scheduled = false;
      let nextStatus = null;
      
      try {
        const t = authService.getToken && authService.getToken();
        const r = await fetch(`/api/baiql/execute/${id}`, { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials:'include' });
        if (r.ok) {
          const j = await r.json();
          if (!mounted) return;
          nextStatus = (j.status || 'queued').toLowerCase();
          
          // Check if execution is terminal before updating state
          if (nextStatus === 'completed' || nextStatus === 'failed') {
            shouldStop = true;
          }
          
          setStatus(j.status || 'queued');
          setBatchType(j.batchType || null);
          setMode(j.mode || 'EXECUTE');
          setCurrent(j.current || 0);
          setTotal(j.total || 0);
          setLastError(j.lastError || null);
          // Only reset screen handling flags when execution transitions from paused to running/completed/failed
          // This prevents re-showing the same screen after it's been handled
          if ((nextStatus === 'running' || nextStatus === 'completed' || nextStatus === 'failed') && previousStatusRef.current === 'paused') {
            // Only reset if we're transitioning away from paused state
            // This allows the same screen to be shown once per pause cycle
            if (screenHandledRef.current) {
              setScreenHandled(false);
              screenHandledRef.current = false;
            }
            // Clear handled signatures when transitioning away from paused to allow new screens
            handledScreenSigs.current.clear();
          }
          // Update refs for next iteration
          previousStatusRef.current = nextStatus;
          screenHandledRef.current = screenHandled;
          
          // Stop polling immediately if terminal - don't fetch logs or stream
          if (shouldStop) {
            return; // Stop polling completely
          }
          
          // Fetch logs snapshot after status update (only if not terminal)
          const rLogs = await fetch(`/api/baiql/execute/${id}/logs`, { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials:'include' });
          if (rLogs.ok) {
            const jl = await rLogs.json();
            if (!mounted) return;
            const raw = Array.isArray(jl.logs) ? jl.logs : [];
            // QUIET mode: reduce visible logs to start/progress/completion
            if ((j.mode || '').toUpperCase() === 'QUIET') {
              const filtered = [];
              // always include the first "Execution started" line if present and BATCH_START
              for (let i = 0; i < raw.length; i++) {
                const msg = String(raw[i] || '');
                if (msg.startsWith('Execution started') || msg.startsWith('BATCH_START')) { filtered.push(msg); break; }
              }
              // include periodic progress snapshots: synthesize one line using current/total
              const cur = j.current || 0; const tot = j.total || 0;
              if (tot > 0) filtered.push(`Progress ${cur}/${tot}`);
              // include terminal status markers if present
              for (let i = raw.length - 1; i >= 0; i--) {
                const msg = String(raw[i] || '');
                if (msg.startsWith('STATUS completed') || msg.startsWith('STATUS failed') || msg.startsWith('STATUS paused')) { filtered.push(msg); break; }
              }
              setLogs(filtered);
            } else {
              setLogs(raw);
            }
          }
          // Fetch stream chunks and detect SCREEN output only while paused and not yet shown final screen
          if (nextStatus === 'paused' && !finalScreenShownRef.current && previousStatusRef.current !== 'completed' && previousStatusRef.current !== 'failed') {
            try {
              const rStream = await fetch(`/api/baiql/execute/${id}/stream`, { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials:'include' });
              if (rStream.ok) {
                const js = await rStream.json();
                const chunks = Array.isArray(js?.chunks) ? js.chunks : [];
                for (let i = chunks.length - 1; i >= 0; i--) {
                  const c = chunks[i];
                  try {
                    const obj = JSON.parse(c);
                    if (obj && obj.type === 'screen') {
                      const sig = `${String(obj.file||'')}|${String(obj.format||'')}|${String(obj.content||'').length}:${String(obj.content||'').slice(0,64)}`;
                      // Only show screen if:
                      // 1. Not already handled in this pause cycle, AND
                      // 2. This signature hasn't been handled before, AND
                      // 3. We haven't shown the final screen yet
                      if (!screenHandledRef.current && !handledScreenSigs.current.has(sig)) {
                        handledScreenSigs.current.add(sig);
                        finalScreenShownRef.current = true; // Mark that we've shown a screen - STOP future screens
                        setScreen({ 
                          show:true, 
                          file:String(obj.file||'results.txt'), 
                          format:String(obj.format||'txt'), 
                          content:String(obj.content||''), 
                          ready:true, 
                          saving:false 
                        });
                        // CRITICAL: Stop polling after showing screen
                        shouldStop = true;
                      }
                      break;
                    }
                  } catch {}
                }
              }
            } catch {}
          }
          
          // Continue polling while not terminal AND we haven't shown final screen
          if (mounted && !shouldStop && !finalScreenShownRef.current) {
            scheduled = true;
            setTimeout(poll, 1500);
          }
          return;
        }
      } catch (err) {
        // On error, only continue polling if still mounted and not terminal
        if (!mounted) return;
      }
      finally {
        // Only schedule next poll if we didn't already schedule one and execution is not terminal
        if (!scheduled && mounted && !shouldStop) {
          setTimeout(poll, 2000);
        }
      }
    };
    poll();

    return () => { mounted = false; clearInterval(tick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Only depend on id - status and screenHandled are tracked via refs to avoid unnecessary re-renders

  const saveScreen = async () => {
    if (!screen.ready) return;
    try {
      setScreen(prev => ({ ...prev, saving:true }));
      const t = authService.getToken && authService.getToken();
      const body = { executionId: id, fileName: screen.file, format: screen.format, content: screen.content };
      const r = await fetch('/api/baiql/screen/save', { method:'POST', headers: { 'Content-Type':'application/json', ...(t ? { Authorization:`Bearer ${t}` } : {}) }, credentials:'include', body: JSON.stringify(body) });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      setToast(prev => ({ ...prev, show:false, persist:false }));
      setToast({ show:true, type:'success', message:`Saved to ${j?.path || 'file'}`, duration:1500, persist:false });
      setScreenHandled(true);
      screenHandledRef.current = true;
      setScreen({ show:false, file:'', format:'txt', content:'', ready:false, saving:false });
      
      // CRITICAL FIX: Only resume if status is 'paused'
      // Do NOT resume if execution is completed or failed
      if (status === 'paused') {
        try { 
          await fetch(`/api/baiql/execute/${id}/resume`, { 
            method:'POST', 
            headers: t ? { Authorization: `Bearer ${t}` } : {}, 
            credentials:'include' 
          }); 
        } catch {}
      }
    } catch (e) {
      setToast({ show:true, type:'error', message:(e && e.message) ? e.message : 'Save failed' });
      setScreen(prev => ({ ...prev, saving:false }));
    }
  };

  const discardScreen = async () => {
    if (!screen.ready) { 
      setScreen({ show:false, file:'', format:'txt', content:'', ready:false, saving:false }); 
      return; 
    }
    try {
      setScreen(prev => ({ ...prev, saving:true }));
      const t = authService.getToken && authService.getToken();
      const body = { executionId: id, fileName: screen.file, format: screen.format, content: screen.content };
      await fetch('/api/baiql/screen/discard', { 
        method:'POST', 
        headers: { 'Content-Type':'application/json', ...(t ? { Authorization:`Bearer ${t}` } : {}) }, 
        credentials:'include', 
        body: JSON.stringify(body) 
      });
      setToast(prev => ({ ...prev, show:false, persist:false }));
      setToast({ show:true, type:'success', message:'Discarded to audit.', duration:1500, persist:false });
      setScreenHandled(true);
      screenHandledRef.current = true;
      setScreen({ show:false, file:'', format:'txt', content:'', ready:false, saving:false });
      
      // CRITICAL FIX: Only call resume if status is actually 'paused'
      // Do NOT resume if execution is completed or failed
      if (status === 'paused') {
        try { 
          await fetch(`/api/baiql/execute/${id}/resume`, { 
            method:'POST', 
            headers: t ? { Authorization: `Bearer ${t}` } : {}, 
            credentials:'include' 
          }); 
        } catch {}
      }
    } catch (e) {
      setToast({ show:true, type:'error', message:(e && e.message) ? e.message : 'Discard failed' });
      setScreen(prev => ({ ...prev, saving:false }));
    }
  };

  const cancel = async () => {
    // Confirm via toast
    setToast({ show:true, type:'warning', message:'Confirm cancel execution?', persist:true, actionLabel:'Confirm', onAction: async () => {
      setToast(prev => ({ ...prev, show:false }));
      try {
        setPending(true);
        const t = authService.getToken && authService.getToken();
        const r = await fetch(`/api/baiql/execute/${id}/cancel`, { method:'POST', headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials:'include' });
        if (r.ok) {
          setStatus('failed');
          setToast({ show:true, type:'error', message:'Execution halted by User', duration:1000 });
        } else {
          setToast({ show:true, type:'error', message:'Cancel failed' });
        }
      } catch {
        setToast({ show:true, type:'error', message:'Cancel failed' });
      } finally { setPending(false); }
    }, secondaryActionLabel:'Keep Running', onSecondaryAction: () => setToast(prev => ({ ...prev, show:false })) });
  };

  const resume = async () => {
    try {
      setPending(true);
      const t = authService.getToken && authService.getToken();
      const r = await fetch(`/api/baiql/execute/${id}/resume`, { method:'POST', headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials:'include' });
      if (r.ok) {
        setStatus('resume');
        setToast({ show:true, type:'success', message:'Resuming...', duration:800 });
      } else {
        setToast({ show:true, type:'error', message:'Resume failed' });
      }
    } catch {
      setToast({ show:true, type:'error', message:'Resume failed' });
    } finally { setPending(false); }
  };

  return (
    <div className="admin-container">
      <div className="admin-section">
        <h2 className="admin-header" style={{ marginTop: 0 }}>BAIQL Execution Monitor</h2>
        <div className="dashboard-card" style={{ padding: 12 }}>
          <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
            <div><strong>ID:</strong> {id}</div>
            <div><strong>Status:</strong> <span style={{ color: status==='completed' ? '#10b981' : status==='failed' ? '#ef4444' : '#f59e0b' }}>{status}</span></div>
            <div><strong>Mode:</strong> {mode}</div>
            <div><strong>Batch Type:</strong> {batchType || '—'}</div>
            <div><strong>Progress:</strong> {current}/{total}</div>
            <div><strong>Elapsed:</strong> {elapsed}s</div>
          </div>
          <div style={{ display:'flex', gap:12, marginTop:12 }}>
            <button className="action-button secondary" onClick={()=>navigate('/baiql')}>Back to Editor</button>
            <button className="action-button" disabled={pending || !(status==='queued' || status==='running')} onClick={cancel}>Cancel</button>
            {status==='paused' && (
              <button className="action-button" disabled={pending} onClick={resume}>Resume</button>
            )}
          </div>
        </div>
        {status === 'failed' && (
          <div className="dashboard-card" style={{ marginTop:12, background:'#fef3c7', border:'1px solid #f59e0b', padding:12 }}>
            <div style={{ color:'#92400e', fontWeight:500, marginBottom:8 }}>⚠ Execution Failed</div>
            {lastError && (
              <div style={{ color:'#78350f', fontSize:13, marginBottom:8, fontFamily:'ui-monospace, monospace' }}>
                Error: {lastError}
              </div>
            )}
            <div style={{ color:'#78350f', fontSize:14 }}>
              If this execution failed due to insufficient balance or credits, please add funds to your account.
            </div>
          </div>
        )}
        <div className="dashboard-card" style={{ marginTop:12 }}>
          <div style={{ padding:12, borderBottom:'1px solid #1f2937' }}><strong>Logs</strong></div>
          <div style={{ padding:12, minHeight:120, fontFamily:'ui-monospace, Menlo, Consolas, monospace', fontSize:13, whiteSpace:'pre-wrap' }}>
            {(logs||[]).length ? logs.join('\n') : '—'}
          </div>
          {status !== 'queued' && status !== 'running' && (
            <div style={{ padding:12, borderTop:'1px solid #1f2937', display:'flex', justifyContent:'flex-end' }}>
              <form method="get" action={`/api/baiql/execute/${id}/download`} style={{ margin:0 }}>
                <button type="submit" className="action-button secondary">
                  Download results (ZIP)
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
      <Toast message={toast.message} type={toast.type} show={toast.show} onClose={() => setToast(prev => ({ ...prev, show:false }))} persist={toast.persist} actionLabel={toast.actionLabel} onAction={toast.onAction} secondaryActionLabel={toast.secondaryActionLabel} onSecondaryAction={toast.onSecondaryAction} />
      {screen.show && (
        <div className="modal-backdrop" style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={(e)=>e.stopPropagation()}>
          <div className="app-modal" role="dialog" aria-modal="true" onClick={(e)=>e.stopPropagation()} style={{ width:'95vw', height:'75vh', background:'#fff', color:'#111', border:'1px solid #e5e7eb', borderRadius:8, display:'flex', flexDirection:'column' }}>
            <div className="app-modal-header" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 12px', borderBottom:'1px solid #e5e7eb' }}>
              <strong>Screen Output Preview ({String(screen.format).toUpperCase()})</strong>
              <div style={{ display:'flex', gap:8 }}>
                <button disabled={!screen.ready || screen.saving} onClick={saveScreen}>Save</button>
                <button disabled={!screen.ready || screen.saving} onClick={discardScreen}>Discard</button>
                <button onClick={discardScreen} title="Close">×</button>
              </div>
            </div>
            <div className="app-modal-body" style={{ padding:12, overflow:'auto', flex:1 }}>
              {(() => {
                const fmt = String(screen.format||'txt').toLowerCase();
                if (fmt === 'json') {
                  try { const obj = JSON.parse(screen.content||''); return (<pre style={{ margin:0, whiteSpace:'pre-wrap' }}>{JSON.stringify(obj, null, 2)}</pre>); } catch { return (<pre style={{ margin:0, whiteSpace:'pre-wrap' }}>{screen.content}</pre>); }
                }
                if (fmt === 'csv') {
                  const rows = (screen.content||'').split(/\r?\n/).filter(Boolean).map(r => r.split(','));
                  return (
                    <div style={{ overflow:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <tbody>
                          {rows.map((r,i)=>(<tr key={i}>{r.map((c,j)=>(<td key={j} style={{ border:'1px solid #e5e7eb', padding:'4px 6px' }}>{c}</td>))}</tr>))}
                        </tbody>
                      </table>
                    </div>
                  );
                }
                return (<pre style={{ margin:0, whiteSpace:'pre-wrap' }}>{screen.content}</pre>);
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BAIQLExecutionMonitor;


